/* ==========================================================================
   kazul-robot-scrollstory-concept — sitewide scroll-driven animation layer
   Everything below is scroll-scrubbed (tied to scroll position, not time) via
   GSAP + ScrollTrigger. The hero's robot-construction sequence lives in its
   own section file; this file covers every section after it. All initial
   "hidden" states are set here at runtime with gsap.set() rather than in CSS,
   so if this script ever fails to load, every section is simply visible in
   its normal resting state instead of stuck invisible.
   ========================================================================== */
(function () {
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

  var prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return; // everything stays in its normal, fully-visible resting state

  gsap.registerPlugin(ScrollTrigger);

  gsap.matchMedia().add(
    { isDesktop: '(min-width: 901px)' },
    function (context) {
      var isDesktop = !!context.conditions.isDesktop;

      // ---------------------------------------------------------------
      // Services — cards wipe into view with a clip-path reveal, staggered
      // ---------------------------------------------------------------
      var serviceCards = gsap.utils.toArray('.service-card');
      if (serviceCards.length) {
        gsap.set(serviceCards, { opacity: 0, y: isDesktop ? 60 : 32, clipPath: 'inset(0% 0 100% 0)' });

        if (isDesktop) {
          gsap.to(serviceCards, {
            opacity: 1,
            y: 0,
            clipPath: 'inset(0% 0 0% 0)',
            ease: 'power2.out',
            stagger: 0.12,
            scrollTrigger: {
              trigger: '.services__grid',
              start: 'top 82%',
              end: 'top 32%',
              scrub: 0.6,
            },
          });
        } else {
          serviceCards.forEach(function (card, i) {
            gsap.to(card, {
              opacity: 1,
              y: 0,
              clipPath: 'inset(0% 0 0% 0)',
              duration: 0.7,
              delay: i * 0.06,
              ease: 'power2.out',
              scrollTrigger: { trigger: card, start: 'top 92%', toggleActions: 'play none none none' },
            });
          });
        }
      }

      // ---------------------------------------------------------------
      // About — heading rises word-by-word behind a mask, then the body
      // copy and pillars settle in with a light stagger
      // ---------------------------------------------------------------
      var aboutHeading = document.querySelector('.about__heading');
      if (aboutHeading && !aboutHeading.hasAttribute('data-split')) {
        aboutHeading.setAttribute('data-split', '1');
        var words = aboutHeading.textContent.trim().split(/\s+/);
        aboutHeading.innerHTML = words
          .map(function (w) { return '<span class="word-mask"><span class="word-inner">' + w + '</span></span>'; })
          .join(' ');
      }
      var wordInners = gsap.utils.toArray('.about__heading .word-inner');
      if (wordInners.length) {
        gsap.set(wordInners, { yPercent: 100 });
        gsap.to(wordInners, {
          yPercent: 0,
          ease: 'power3.out',
          stagger: 0.035,
          scrollTrigger: { trigger: '.about__heading', start: 'top 88%', end: 'top 58%', scrub: 0.5 },
        });
      }

      var aboutFade = [document.querySelector('.about__eyebrow'), document.querySelector('.about__text')].filter(Boolean);
      if (aboutFade.length) {
        gsap.set(aboutFade, { opacity: 0, y: 22 });
        gsap.to(aboutFade, {
          opacity: 1,
          y: 0,
          ease: 'power2.out',
          stagger: 0.1,
          scrollTrigger: { trigger: '.about__inner', start: 'top 85%', end: 'top 60%', scrub: 0.5 },
        });
      }

      var pillars = gsap.utils.toArray('.about-pillar');
      if (pillars.length) {
        gsap.set(pillars, { opacity: 0, y: 34 });
        gsap.to(pillars, {
          opacity: 1,
          y: 0,
          ease: 'power2.out',
          stagger: 0.1,
          scrollTrigger: { trigger: '.about__pillars', start: 'top 88%', end: 'top 55%', scrub: 0.6 },
        });
      }

      // ---------------------------------------------------------------
      // Pricing / plans — cards scale up from a slightly smaller, dimmer
      // state as they cross into view (used on the homepage grid and the
      // standalone collection page — both share the .product-grid markup)
      // ---------------------------------------------------------------
      var planCards = gsap.utils.toArray('.product-grid .product-card');
      if (planCards.length) {
        gsap.set(planCards, { opacity: 0, scale: 0.9, y: 28 });

        if (isDesktop) {
          gsap.to(planCards, {
            opacity: 1,
            scale: 1,
            y: 0,
            ease: 'power2.out',
            stagger: 0.1,
            scrollTrigger: { trigger: '.product-grid', start: 'top 85%', end: 'top 40%', scrub: 0.6 },
          });
        } else {
          planCards.forEach(function (card, i) {
            gsap.to(card, {
              opacity: 1,
              scale: 1,
              y: 0,
              duration: 0.6,
              delay: i * 0.06,
              ease: 'power2.out',
              scrollTrigger: { trigger: card, start: 'top 92%', toggleActions: 'play none none none' },
            });
          });
        }
      }

      // ---------------------------------------------------------------
      // Voice showcase — the card scales in; a soft glow drifts behind it
      // at a slower speed than the scroll for a touch of depth
      // ---------------------------------------------------------------
      var kzvsCard = document.querySelector('[data-kzvs-card]');
      if (kzvsCard) {
        gsap.set(kzvsCard, { opacity: 0, scale: 0.88, y: 26 });
        gsap.to(kzvsCard, {
          opacity: 1,
          scale: 1,
          y: 0,
          ease: 'power2.out',
          scrollTrigger: { trigger: kzvsCard, start: 'top 85%', end: 'top 50%', scrub: 0.6 },
        });

        var kzvsInner = kzvsCard.parentElement;
        var glow = kzvsInner.querySelector('.kzvs__glow');
        if (!glow) {
          glow = document.createElement('div');
          glow.className = 'kzvs__glow';
          glow.setAttribute('aria-hidden', 'true');
          kzvsInner.insertBefore(glow, kzvsCard);
        }
        if (isDesktop) {
          gsap.to(glow, {
            y: -50,
            ease: 'none',
            scrollTrigger: { trigger: '.kzvs', start: 'top bottom', end: 'bottom top', scrub: 0.8 },
          });
        }
      }

      var kzvsFade = [
        document.querySelector('.kzvs__brandmark'),
        document.querySelector('.kzvs__eyebrow'),
        document.querySelector('.kzvs__heading'),
        document.querySelector('.kzvs__subtext'),
      ].filter(Boolean);
      if (kzvsFade.length) {
        gsap.set(kzvsFade, { opacity: 0, y: 20 });
        gsap.to(kzvsFade, {
          opacity: 1,
          y: 0,
          ease: 'power2.out',
          stagger: 0.08,
          scrollTrigger: { trigger: '.kzvs__inner', start: 'top 85%', end: 'top 55%', scrub: 0.5 },
        });
      }

      // ---------------------------------------------------------------
      // Reviews — cards settle in with a light scale + depth stagger
      // ---------------------------------------------------------------
      var reviewCards = gsap.utils.toArray('.reviews__card');
      if (reviewCards.length) {
        gsap.set(reviewCards, { opacity: 0, scale: 0.92, y: 32 });

        if (isDesktop) {
          gsap.to(reviewCards, {
            opacity: 1,
            scale: 1,
            y: 0,
            ease: 'power2.out',
            stagger: 0.12,
            scrollTrigger: { trigger: '.reviews__grid', start: 'top 85%', end: 'top 45%', scrub: 0.6 },
          });
        } else {
          reviewCards.forEach(function (card, i) {
            gsap.to(card, {
              opacity: 1,
              scale: 1,
              y: 0,
              duration: 0.6,
              delay: i * 0.07,
              ease: 'power2.out',
              scrollTrigger: { trigger: card, start: 'top 92%', toggleActions: 'play none none none' },
            });
          });
        }
      }

      // ---------------------------------------------------------------
      // Contact — kept calm: one gentle settle, no stagger, no scale
      // ---------------------------------------------------------------
      var contactInner = document.querySelector('.contact__inner');
      if (contactInner) {
        gsap.set(contactInner, { opacity: 0, y: 24 });
        gsap.to(contactInner, {
          opacity: 1,
          y: 0,
          ease: 'power1.out',
          scrollTrigger: { trigger: contactInner, start: 'top 88%', end: 'top 65%', scrub: 0.4 },
        });
      }

      // ---------------------------------------------------------------
      // FAQ — also calm: intro settles, then each question eases in
      // ---------------------------------------------------------------
      var faqIntro = [document.querySelector('.faq__eyebrow'), document.querySelector('.faq__heading')].filter(Boolean);
      if (faqIntro.length) {
        gsap.set(faqIntro, { opacity: 0, y: 18 });
        gsap.to(faqIntro, {
          opacity: 1,
          y: 0,
          ease: 'power1.out',
          stagger: 0.08,
          scrollTrigger: { trigger: '.faq__inner', start: 'top 88%', end: 'top 68%', scrub: 0.4 },
        });
      }

      var faqItems = gsap.utils.toArray('.faq__item');
      if (faqItems.length) {
        gsap.set(faqItems, { opacity: 0, y: 20 });
        gsap.to(faqItems, {
          opacity: 1,
          y: 0,
          ease: 'power1.out',
          stagger: 0.06,
          scrollTrigger: { trigger: '.faq__list', start: 'top 90%', end: 'top 55%', scrub: 0.5 },
        });
      }
    }
  );

  // Web fonts and product images can arrive after ScrollTrigger has already
  // measured section positions, shifting layout underneath it. Re-measure
  // once fonts are ready and once more on full load, so trigger points stay
  // accurate without needing a manual scroll/resize first.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () { ScrollTrigger.refresh(); });
  }
  window.addEventListener('load', function () { ScrollTrigger.refresh(); });
})();
